import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { PRODUCT_PDF_MAX_BYTES, ProductPdfFile } from '../../services/sprayService';

/**
 * Generic PDF attachment control used for both the product Label and MSDS
 * slots. The component owns:
 *   - Client-side validation (PDF MIME + 10 MB ceiling),
 *   - The "empty" / "populated" rendering switch,
 *   - The hidden <input type="file"> trigger,
 *   - Transient loading and error state.
 *
 * Persistence is delegated to the parent via `onUpload(file)` and `onRemove()`.
 */
export interface PdfUploaderProps {
  file?: ProductPdfFile;
  /** i18n key for the section label (e.g. 'spray.products.labelPdf'). */
  labelKey: string;
  /** i18n key for the empty-state helper text. */
  uploadPromptKey: string;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
  /**
   * Optional override for the View action. Defaults to opening `file.url`
   * in a new tab. Parents may inject a signed-URL resolver here.
   */
  onView?: (file: ProductPdfFile) => void;
  disabled?: boolean;
}

const PdfUploader: React.FC<PdfUploaderProps> = ({
  file,
  labelKey,
  uploadPromptKey,
  onUpload,
  onRemove,
  onView,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>('');
  const [busy, setBusy] = useState(false);

  const clickInput = () => {
    setError('');
    inputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files && e.target.files[0];
    // Reset the input value so selecting the same file twice re-triggers change.
    e.target.value = '';
    if (!selected) return;

    if (selected.type !== 'application/pdf') {
      setError(t('spray.products.pdfErrors.notPdf'));
      return;
    }
    if (selected.size > PRODUCT_PDF_MAX_BYTES) {
      setError(t('spray.products.pdfErrors.tooLarge'));
      return;
    }

    setError('');
    setBusy(true);
    try {
      await onUpload(selected);
    } catch (err: any) {
      setError(err?.message || t('spray.products.pdfErrors.uploadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setError('');
    setBusy(true);
    try {
      await onRemove();
    } catch (err: any) {
      setError(err?.message || t('spray.products.pdfErrors.removeFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleView = () => {
    if (!file) return;
    if (onView) {
      onView(file);
      return;
    }
    window.open(file.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="pdf-uploader border rounded p-3">
      <div className="fw-semibold mb-2">{t(labelKey)}</div>

      {error && (
        <Alert variant="danger" role="alert" className="py-2 small">
          {error}
        </Alert>
      )}

      <input
        ref={inputRef}
        data-testid="pdf-uploader-input"
        type="file"
        accept="application/pdf"
        hidden
        onChange={handleFileChange}
      />

      {!file ? (
        <div className="d-flex flex-column align-items-start gap-2">
          <div className="text-muted small">{t(uploadPromptKey)}</div>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={clickInput}
            disabled={disabled || busy}
          >
            {busy ? <Spinner animation="border" size="sm" /> : t('spray.products.pdfActions.upload')}
          </Button>
        </div>
      ) : (
        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <span className="text-truncate" title={file.originalName}>
            {file.originalName}
          </span>
          <div className="d-flex gap-2">
            <Button type="button" variant="outline-secondary" size="sm" onClick={handleView} disabled={busy}>
              {t('spray.products.pdfActions.view')}
            </Button>
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              onClick={clickInput}
              disabled={disabled || busy}
            >
              {t('spray.products.pdfActions.replace')}
            </Button>
            <Button
              type="button"
              variant="outline-danger"
              size="sm"
              onClick={handleRemove}
              disabled={disabled || busy}
            >
              {t('spray.products.pdfActions.remove')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfUploader;
